namespace SolovisGateway.Admin.Organizations {

    export class SsoConnectionStatus {
        public static Draft = "Draft";
        public static TestLinkGenerated = "Test Link Generated";
        public static Enabled = "Enabled";
        public static Disabled = "Disabled";
        public static ReadOnly = "Read-only";
    }

    export interface SsoConnectionStatusBadgeOptions {
        status: string;
        isReadOnly?: boolean;
    }

    export interface CertExpiryTagOptions {
        expiresAt: number | null;
    }

    // Configured support contact, threaded down from GlobalConfig (SupportContactConfig) via
    // ViewBag on ManageConnections/ConfigureConnection. Both fields must be set for the link to
    // render - if either is missing, every consumer falls back to plain, non-linked text.
    export interface SupportContactOptions {
        supportContactText?: string;
        supportContactLink?: string;
    }

    export class SsoConnectionStatusUi {
        private static readonly CertExpiryWarningDays = 30;

        // Always-rendered base instruction, regardless of whether a support contact is configured -
        // "leadIn" is never dropped the way it used to be; only this clause's "raise a ticket on
        // <link> or " lead-in is conditional. Mirrors the pattern used by LoginError.cshtml and the
        // Auth0 email templates (which use "Portal admin" instead of "administrator" - deliberately
        // different wording per surface, not a shared string).
        public static readonly SupportContactBaseTail = "reach out to your administrator";

        // Builds "<leadIn>raise a ticket on <a>supportContactText</a> or <baseTail><trailingSuffix>"
        // when a support contact is configured, or "<leadIn><baseTail><trailingSuffix>" when it
        // isn't - leadIn always renders either way. Returns nodes to append in order - every call
        // site here builds its message by appending a sequence of <span>/<a> nodes rather than
        // setting a single text string, so a real anchor can render inline.
        public static buildSupportContactNodes(options: SupportContactOptions, leadIn: string, trailingSuffix: string = "."): JQuery[] {
            let nodes: JQuery[] = [$("<span>").text(leadIn)];

            if (options.supportContactText && options.supportContactLink) {
                nodes.push(
                    $("<span>").text("please raise a ticket on "),
                    $("<a>")
                        .attr("href", options.supportContactLink)
                        .attr("target", "_blank")
                        .attr("rel", "noopener noreferrer")
                        .text(options.supportContactText),
                    $("<span>").text(" or " + SsoConnectionStatusUi.SupportContactBaseTail + trailingSuffix)
                );
            }
            else {
                nodes.push($("<span>").text("please " + SsoConnectionStatusUi.SupportContactBaseTail + trailingSuffix));
            }

            return nodes;
        }

        // Convenience wrapper for the common case: append the built nodes straight onto a container.
        public static appendSupportContactLine(container: JQuery, options: SupportContactOptions, leadIn: string, trailingSuffix: string = "."): void {
            SsoConnectionStatusUi.buildSupportContactNodes(options, leadIn, trailingSuffix).forEach(function (node) {
                container.append(node);
            });
        }

        private static appendPill(container: JQuery, text: string, borderColor: string, color: string, backgroundColor: string, borderStyle: string = "solid"): void {
            $("<span>").text(text).css({
                "border": "1px " + borderStyle + " " + borderColor,
                "color": color,
                "background-color": backgroundColor,
                "padding": "3px 8px",
                "border-radius": "3px",
                "font-size": "12px",
                "font-weight": "bold"
            }).appendTo(container);
        }

        public static appendCertExpiryTag(container: JQuery, options: CertExpiryTagOptions): void {
            if (options.expiresAt == null) {
                $("<span>").text("Not available").css({ color: "#999" }).appendTo(container);
                return;
            }

            let expiresDate = new Date(options.expiresAt);
            $("<span>").text(DevExpress.localization.formatDate(expiresDate, "shortDate")).appendTo(container);

            let daysUntilExpiry = (options.expiresAt - Date.now()) / (1000 * 60 * 60 * 24);

            if (daysUntilExpiry < 0) {
                SsoConnectionStatusUi.appendPill(container, "Expired", "#e74c3c", "#c0392b", "#fdecea");
            }
            else if (daysUntilExpiry <= SsoConnectionStatusUi.CertExpiryWarningDays) {
                SsoConnectionStatusUi.appendPill(container, "Expiring soon", "#f39c12", "#b9770e", "#fef5e7");
            }
        }
        public static getDisplayStatus(status: string): string {
            return status === SsoConnectionStatus.TestLinkGenerated
                ? SsoConnectionStatus.Draft
                : status;
        }

        public static appendStatusBadge(container: JQuery, options: SsoConnectionStatusBadgeOptions): void {
            let statusStyles = {
                borderColor: "#95a5a6",
                color: "#95a5a6",
                backgroundColor: "#f5f5f5",
                borderStyle: "solid"
            };
            let displayText = SsoConnectionStatusUi.getDisplayStatus(options.status);

            if (options.isReadOnly) {
                statusStyles = {
                    borderColor: "#95a5a6",
                    color: "#95a5a6",
                    backgroundColor: "transparent",
                    borderStyle: "dashed"
                };
                displayText = SsoConnectionStatus.ReadOnly;
            }
            else {
                switch (options.status) {
                    case SsoConnectionStatus.Enabled:
                        statusStyles = { borderColor: "#18BC9C", color: "#18BC9C", backgroundColor: "#f0faf7", borderStyle: "solid" };
                        break;
                    case SsoConnectionStatus.Draft:
                    case SsoConnectionStatus.TestLinkGenerated:
                        statusStyles = { borderColor: "#3498DB", color: "#2c7fbd", backgroundColor: "#f0f7fd", borderStyle: "solid" };
                        break;
                    case SsoConnectionStatus.Disabled:
                        statusStyles = { borderColor: "#95a5a6", color: "#95a5a6", backgroundColor: "#f5f5f5", borderStyle: "solid" };
                        break;
                }
            }

            SsoConnectionStatusUi.appendPill(
                container,
                displayText,
                statusStyles.borderColor,
                statusStyles.color,
                statusStyles.backgroundColor,
                statusStyles.borderStyle
            );
        }
    }
}
